import { useContext, useEffect, useMemo, useState } from 'react';
import { ApiContext } from '../contexts/ApiContext';
import { getProductById } from '../service/ApiLeiten';

/**
 * Trae los productos referenciados por los campos de ids de un producto
 * (spare_part_ids / accessory_ids).
 *
 * Reemplaza el mismo useEffect de ~50 líneas que estaba copiado en ProductCard,
 * ProductCardEspecial y ProductCardKit. La lista de kits ahora sale del context
 * (una sola request para toda la app) en lugar de pedirse en cada card.
 */
export function useProductsByIds({ product, type }) {
    const { kitMaintenanceIds } = useContext(ApiContext);

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    const ids = useMemo(() => {
        const raw = type === 'accessory' ? product?.accessory_ids : product?.spare_part_ids;
        return raw ? raw.split(',').map(id => id.trim()).filter(Boolean) : [];
    }, [product, type]);

    // Para los kits hay que esperar la lista de kits antes de poder filtrar.
    const waitingKitIds = type === 'kit' && !kitMaintenanceIds;

    useEffect(() => {
        if (waitingKitIds) return;

        const wanted = type === 'kit' ? ids.filter(id => kitMaintenanceIds.has(id)) : ids;

        if (wanted.length === 0) {
            setProducts([]);
            return;
        }

        let cancelled = false;
        setLoading(true);

        // allSettled y no all: un id roto no debería vaciar la lista entera.
        Promise.allSettled(wanted.map(id => getProductById(id)))
            .then(results => {
                if (cancelled) return;

                const loaded = results
                    .filter(result => result.status === 'fulfilled')
                    .map(result => result.value?.data)
                    .filter(item => item?.producto);

                const failed = results.length - loaded.length;
                if (failed > 0) {
                    console.warn(`No se pudieron cargar ${failed} de ${results.length} productos.`);
                }

                // Los kits ya vienen filtrados por la lista de kits, no se reordenan.
                if (type !== 'kit') {
                    loaded.sort((a, b) =>
                        String(a.producto.modelo ?? '').localeCompare(String(b.producto.modelo ?? ''))
                    );
                }

                setProducts(loaded);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [ids, type, kitMaintenanceIds, waitingKitIds]);

    return { products, loading: loading || waitingKitIds };
}
